<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { Icon, Dropdown, Button, ProfilePicture } from '@components'
import { getPrice, sortedDays } from '@utils'

import moment from 'moment'

import { useAppointment } from '@store/appointment'
const $appointment = useAppointment()

import { useSettings } from '@store/settings'
const $settings = useSettings()

const props = defineProps({
    appointment: Object,
    rightSidebar: Boolean,
    moment: Function,
    route: Object,
})

const deleteAppointment = async (appointment) => {
    if (!appointment.appointment_id) return Toast.error('No appointment id found')

    const response = await props.appointment.delete(appointment.appointment_id)

    if (response.success) {
        Toast.success('Appointment deleted successfully')
        await $appointment.fetchData()
    } else {
        Toast.error(response.message)
    }
}

const remainingDuration = ref('')

const calculateRemainingDuration = () => {
    const bookingDate = moment(props.appointment.booking_date, 'YYYY-MM-DD')
    const startTime = moment(props.appointment.booking_time_start, 'HH:mm')
    const endTime = moment(props.appointment.booking_time_end, 'HH:mm')

    const now = moment()

    const startDateTime = moment(
        `${bookingDate.format('YYYY-MM-DD')} ${startTime.format('HH:mm')}`,
        'YYYY-MM-DD HH:mm'
    )

    const endDateTime = moment(
        `${bookingDate.format('YYYY-MM-DD')} ${endTime.format('HH:mm')}`,
        'YYYY-MM-DD HH:mm'
    )

    let targetTime

    if (now.isBefore(startDateTime)) {
        targetTime = startDateTime
    } else {
        targetTime = endDateTime
    }

    const diffInSeconds = targetTime.diff(now, 'seconds')

    if (diffInSeconds <= 0) {
        remainingDuration.value = 'Expired'
        return
    }

    const diffInDays = Math.floor(diffInSeconds / (60 * 60 * 24))
    const diffInHours = Math.floor((diffInSeconds % (60 * 60 * 24)) / (60 * 60))
    const diffInMinutes = Math.floor((diffInSeconds % (60 * 60)) / 60)
    const diffInRemainingSeconds = diffInSeconds % 60

    if (diffInDays > 0) {
        remainingDuration.value = `${diffInDays} day${diffInDays > 1 ? 's' : ''}`
    } else if (diffInHours > 0) {
        remainingDuration.value = `${diffInHours} hr${diffInHours > 1 ? 's' : ''}`
    } else if (diffInMinutes > 0) {
        remainingDuration.value = `${diffInMinutes + 1} min${diffInMinutes > 1 ? 's' : ''}`
    } else {
        remainingDuration.value = `${diffInRemainingSeconds} sec${diffInRemainingSeconds > 10 ? 's' : ''}`
    }
}


let intervalId

onMounted(() => {
    calculateRemainingDuration()
    intervalId = setInterval(calculateRemainingDuration, 1000)
})

onBeforeUnmount(() => {
    clearInterval(intervalId)
})
</script>

<template>
    <div class="m-[0_auto]" v-if="!rightSidebar">
        <!-- <div :style="`background-image: url(${appointment.customer?.meta?.profile_image_url ? appointment.customer?.meta?.profile_image_url : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'});`" class="relative w-[48px] h-[48px] bg-center bg-no-repeat bg-cover rounded-full">
            <div :style="`background-image: url(${appointment.staff?.meta?.profile_image_url ? appointment.staff?.meta?.profile_image_url : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'});`" class="absolute -bottom-[6px] -right-[6px] w-[24px] h-[24px] border-[3px] border-white bg-no-repeat bg-cover rounded-full"></div>
        </div> -->
        <ProfilePicture :name="appointment.customer?.name" :img="appointment.customer?.meta?.profile_image_url" class="w-[48px] h-[48px]" :secondary="true" :secondaryImg="appointment.staff?.meta?.profile_image_url" :secondaryName="appointment.staff?.name" :secondaryBgColor="appointment.staff?.meta?.color" secondaryImgContainer="!w-[24px] !h-[24px]" secondaryImgClass="border-2 border-white" secondaryTextClass="!text-sm !leading-none border-2 border-white" />
    </div>
    <div v-if="rightSidebar" class="bg-white p-3 rounded-xl border-[1px] border-[#EAECF0]">
        <div class="flex items-center justify-between w-full gap-3">
            <div class="flex flex-col items-center gap-3 md:flex-row">
                <!-- <div :style="`background-image: url(${appointment.customer?.meta?.profile_image_url ? appointment.customer?.meta?.profile_image_url : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'});`" class="relative w-[60px] h-[60px] bg-center bg-no-repeat bg-cover rounded-full">
                    <div :style="`background-image: url(${appointment.staff?.meta?.profile_image_url ? appointment.staff?.meta?.profile_image_url : 'https://cdn-icons-png.flaticon.com/512/149/149071.png'});`" class="absolute -bottom-[6px] -right-[6px] w-[30px] h-[30px] border-[3px] border-white bg-no-repeat bg-cover rounded-full"></div>
                </div> -->
                <ProfilePicture :name="appointment.customer?.name" :img="appointment.customer?.meta?.profile_image_url" class="w-[60px] h-[60px]" :secondary="true" :secondaryImg="appointment.staff?.meta?.profile_image_url" :secondaryName="appointment.staff?.name" :secondaryBgColor="appointment.staff?.meta?.color" secondaryImgContainer="!w-[30px] !h-[30px]" secondaryImgClass="border-2 border-white" secondaryTextClass="border-2 border-white" />

                <div>
                    <div class="font-semibold text-base text-[#344054] mb-1 flex gap-2 items-center">
                        {{ appointment.customer?.name || 'N/A' }}
                        <Icon v-if="appointment.customer?.meta?.priority == 1" name="star01" size="18px" class="text-[#7F56D9] border-[2px] border-[#7F56D9] rounded-full p-[2px]" style="fill:#7F56D9;"></Icon>
                    </div>
                    <div class="flex flex-col gap-2 md:flex-row items-center">
                        <div class="text-xs text-[#344054] text-nowrap text-ellipsis">{{ appointment.meta?.service_name }}</div>
                        <Icon name="ellipse01" size="4px" class="text-[#D9D9D9]"></Icon>
                        <div class="text-xs text-[#344054] text-nowrap">
                            {{ moment(appointment.booking_time_start, 'HH:mm').format('hh:mm A') }} - {{ moment(appointment.booking_time_end, 'HH:mm').format('hh:mm A') }}
                        </div>
                        <Icon v-if="!$settings.addons.payment" name="ellipse01" size="4px" class="text-[#D9D9D9]"></Icon>
                        <div v-if="!$settings.addons.payment" class="text-xs text-[#344054] text-nowrap">
                            {{ getPrice(appointment.meta.price) }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="lg:w-[130px] flex items-center justify-end gap-4 whitespace-nowrap">
                <div @click="appointment.meta.checkbox = !appointment.meta.checkbox">
                    <Icon :name="appointment.meta.checkbox ? 'chevronUp' : 'chevronDown'" size="36px" class="text-[#344054] transition-all duration-300 cursor-pointer p-[7px] border border-[#D0D5DD] rounded-full"></Icon>
                </div>

                <Dropdown :contentClass="`w-56 top-[30px] w-[117px] z-[9] ${$settings.isRTL ? 'left-0' : 'right-0'}`">
                    <Icon name="dotsVertical" size="24px" class="text-[#98A2B3] cursor-pointer" />

                    <template #content>
                        <div class="flex flex-col gap-1">
                            <div class="rounded-md mx-1.5 my-0.5 transition">
                                <router-link v-if="access('appointment.edit')" :to="{ name: 'appointment.edit', params: { appointmentId: appointment.appointment_id }, query: { backTo: route.fullPath } }" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                    <Icon size="16px" name="edit05" />
                                    <div class="text-[#344054] text-sm font-medium">Edit</div>
                                </router-link>
                            </div>
                            <div class="rounded-md mx-1.5 my-0.5 transition">
                                <div @click="deleteAppointment(appointment)" class="px-2.5 py-[9px] flex gap-2 items-center hover:bg-[#F9FAFB] rounded-md cursor-pointer transition-all duration-300">
                                    <Icon size="16px" name="trash01" />
                                    <div class="text-[#344054] text-sm font-medium">Delete</div>
                                </div>
                            </div>
                        </div>
                    </template>
                </Dropdown>
            </div>
        </div>
        <div v-if="appointment.meta.checkbox" class="bg-[#EAECF0] h-[1px] my-4"></div>
        <Transition name="slide-fade" v-if="appointment.meta.checkbox">
            <div class="flex flex-col items-center justify-between gap-3 md:flex-row">
                <div class="flex flex-col items-center gap-3 md:flex-row">
                    <div class="w-[100px] flex items-center gap-1 text-[#344054] font-semibold">
                        <Icon name="clockRewind" size="20px"></Icon>
                        {{ remainingDuration }}
                    </div>
                    <div v-if="$settings.addons.payment" class="flex gap-2">
                        <div class="w-[64px]" :class="{ 'border-l border-l-[#EAECF0] pl-2': index !== 0 }" v-for="(item, index) in [
                            { name: 'Paid', price: appointment.total_paid },
                            { name: 'Due', price: appointment.total_due },
                            { name: 'Total', price: appointment.meta.price },
                        ]">
                            <div class="text-[#667085] text-xs mb-[6px]">{{ item.name }}</div>
                            <div class="text-[#101828] text-xs font-semibold">{{ getPrice(item.price) }}</div>
                        </div>
                    </div>
                </div>
                <div v-if="appointment.total_due == 0 && $settings.addons.payment" class="text-[#099250] flex items-center gap-2">
                    <Icon name="check" size="20px"></Icon>
                    Paid
                </div>
                <Button v-if="appointment.total_due > 0 && $settings.addons.payment" size="sm" type="primary" radius="square" class="text-nowrap" @click="$router.push({ name: 'appointment.edit', params: { appointmentId: appointment.appointment_id }, query: { backTo: route.fullPath, tab: 'payment' } })">Collect {{ $settings.labels.payment?.singular }}</Button>
            </div>
        </Transition>
    </div>
</template>